/**
 * What a cell shows when there is no value **and there is a reason**.
 *
 * A blank cell reads as broken, and once columns arrive in two waves a blank
 * means two different things — *not yet* and *never* — drawn with the same
 * pixels. So neither is left blank, and the two are not drawn alike:
 *
 * - **pending** — no value has arrived yet, and one is coming. A row of dots,
 *   the ordinary shape of "shortly", said quietly so a screen of them does not
 *   shout.
 * - **unavailable** — nothing is going to send this. It says so in words,
 *   because a user looking at a column that will never fill needs to be told
 *   that rather than left to wait it out.
 *
 * Installed by `ScreenerGrid` only for a column that is in one of those states,
 * so `ready` never reaches here and every cell of the column agrees — the state
 * is a fact about the column, not about the row. A *null value in a ready
 * column* is a different thing entirely: it is a computed answer of null, and it
 * stays blank, because teaching this surface to draw nulls as "waiting" is the
 * lesson `docs/plans/screener-fast-path.md` says is expensive to unteach.
 *
 * `var(--qf-*)` only — this island never owns a theme (D18).
 */

import type { CSSProperties } from "react";

import type { ColumnState } from "../lib/column-universe.js";

export interface ColumnStateCellParams {
  state: ColumnState;
}

const MUTED: CSSProperties = {
  color: "var(--qf-text-muted)",
  fontSize: "0.72rem",
};

/** Dots, spaced out so they read as a wait rather than as an ellipsis in prose. */
const PENDING: CSSProperties = { ...MUTED, letterSpacing: "0.12em" };

/** Italic, because it is a note about the column rather than a value in it. */
const UNAVAILABLE: CSSProperties = { ...MUTED, fontStyle: "italic" };

export function ColumnStateCell({ state }: ColumnStateCellParams) {
  if (state === "pending") {
    return (
      <span style={PENDING} title="Waiting for this column's first values.">
        •••
      </span>
    );
  }
  if (state === "unavailable") {
    return (
      <span style={UNAVAILABLE} title="No service is computing this column.">
        no source
      </span>
    );
  }
  return null;
}
